import { Outlet, NavLink, useNavigate } from "react-router";
import { useSnapshot } from "valtio";
import {
  LayoutDashboard,
  Users,
  Settings,
  LogOut,
  Menu,
  Shield,
  BarChart3,
  Zap,
  Flag,
  Bug,
  AppWindow,
  Mic,
  QrCode,
  Play,
  CreditCard,
} from "lucide-react";
import { db } from "@/lib/instantdb";
import { cn } from "@/lib/utils";
import {
  uiStore,
  toggleSidebar,
  closeMobileSidebar,
  toggleMobileSidebar,
} from "@/lib/stores/ui";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/users", icon: Users, label: "Users" },
  { path: "/roles", icon: Shield, label: "Roles" },
  { path: "/apps", icon: AppWindow, label: "OAuth Apps" },
  { path: "/voices", icon: Mic, label: "Voice Library" },
  { path: "/credits", icon: CreditCard, label: "AI Credits" },
  { path: "/guest-uploads", icon: QrCode, label: "Guest Uploads" },
  { path: "/flags", icon: Flag, label: "Feature Flags" },
  { path: "/bugs", icon: Bug, label: "Bug Reports" },
  { path: "/jobs", icon: Play, label: "Jobs" },
];

const toolNavItems = [
  { path: "/gateway", icon: Zap, label: "AI Gateway" },
  { path: "/analytics", icon: BarChart3, label: "Analytics" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export default function Layout() {
  const { user } = db.useAuth();
  const { sidebarCollapsed, mobileSidebarOpen } = useSnapshot(uiStore);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await db.auth.signOut();
    navigate("/login");
  };

  const handleNavClick = () => {
    closeMobileSidebar();
  };

  // Shared navigation content for both mobile and desktop
  const NavigationContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <>
      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-2">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/"}
            onClick={isMobile ? handleNavClick : undefined}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-accent",
              )
            }
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            <span
              className={cn(
                isMobile ? "inline" : "md:hidden",
                !sidebarCollapsed && "md:inline",
              )}
            >
              {item.label}
            </span>
          </NavLink>
        ))}

        {/* Tools Section */}
        <Separator className="my-2" />
        {toolNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={isMobile ? handleNavClick : undefined}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-accent",
              )
            }
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            <span
              className={cn(
                isMobile ? "inline" : "md:hidden",
                !sidebarCollapsed && "md:inline",
              )}
            >
              {item.label}
            </span>
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="border-t border-border p-2">
        <div
          className={cn(
            "flex items-center gap-3 rounded-lg px-3 py-2",
            !isMobile && sidebarCollapsed ? "md:justify-center" : "",
          )}
        >
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
            {user?.email?.[0].toUpperCase() ?? "?"}
          </div>
          <div
            className={cn(
              "flex-1 truncate",
              !isMobile && "md:hidden",
              !isMobile && !sidebarCollapsed && "md:block",
            )}
          >
            <p className="truncate text-sm font-medium text-foreground">
              {user?.email}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            handleSignOut();
            if (isMobile) closeMobileSidebar();
          }}
          className={cn(
            "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent",
            !isMobile && sidebarCollapsed ? "md:justify-center" : "",
          )}
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          <span
            className={cn(
              isMobile ? "inline" : "md:hidden",
              !sidebarCollapsed && "md:inline",
            )}
          >
            Sign Out
          </span>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-full bg-background">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>

      {/* Mobile Layout - vertical flex column */}
      <div className="flex min-h-0 flex-1 flex-col md:hidden">
        {/* Safe area spacer - white background for area behind Dynamic Island */}
        <div
          className="flex-shrink-0 bg-card"
          style={{ height: "var(--safe-area-top)" }}
        />

        {/* Mobile Header - in document flow */}
        <header className="relative flex h-14 flex-shrink-0 items-center justify-between border-b border-border bg-card px-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMobileSidebar}
            aria-label="Toggle menu"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-1.5">
            <span className="text-xl font-bold text-primary">Vizi Admin</span>
          </div>
          <div className="w-10" /> {/* Spacer for alignment */}
        </header>

        {/* Mobile Main Content */}
        <main
          id="main-content"
          className="min-h-0 flex-1 overflow-y-auto bg-background"
        >
          <Outlet />
        </main>

        {/* Safe area spacer - bottom */}
        <div
          className="flex-shrink-0 bg-background"
          style={{ height: "var(--safe-area-bottom)" }}
        />
      </div>

      {/* Mobile Sidebar - using Drawer */}
      <Drawer
        open={mobileSidebarOpen}
        onOpenChange={(open) => !open && closeMobileSidebar()}
        direction="left"
      >
        <DrawerContent
          className="flex w-64 flex-col p-0"
          style={{
            paddingTop: "var(--safe-area-top)",
            paddingBottom: "var(--safe-area-bottom)",
          }}
        >
          <DrawerHeader className="border-b border-border px-4 py-4">
            <DrawerTitle className="flex items-center gap-1.5 text-left text-xl font-bold text-primary">
              Vizi Admin
            </DrawerTitle>
          </DrawerHeader>
          <NavigationContent isMobile />
        </DrawerContent>
      </Drawer>

      {/* Desktop Layout - horizontal flex with sidebar */}
      <div className="hidden min-h-0 flex-1 md:flex">
        {/* Desktop Sidebar */}
        <aside
          className={cn(
            "flex flex-col border-r border-border bg-card",
            sidebarCollapsed ? "w-16" : "w-64",
            "transition-all duration-200",
          )}
        >
          {/* Logo */}
          <div className="flex h-16 items-center justify-between border-b border-border px-4">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-1.5">
                <span className="text-xl font-bold text-primary">
                  Vizi Admin
                </span>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
          <NavigationContent />
        </aside>

        {/* Desktop Main Content */}
        <main
          id="main-content-desktop"
          className="min-h-0 flex-1 overflow-y-auto"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
